<template lang="pug">
  #challenge-builder-type
    .level.is-flex-wrap-wrap
      .level-left.is-flex-wrap-wrap.max-witdh-100.is-justify-content-center
        .level-item
          WtTitle(h2) Step 1: Select Your Challenge
      .level-right.is-flex-grow-1.is-justify-content-center
        .level-item(v-if="isAdminBuilder")
          BField.search-map-field(type="is-primary", class="wider-field" )
            BInput(
              placeholder="Search challenges"
              custom-class="is-rounded"
              type="search"
              icon="magnify"
              v-model="mapQuery"
              @input="triggerMapSearch()"
            )
        .level-item(v-if="displayMapTypeDropdown")
          BDropdown.filter-map-field(
            v-model="mapType"
            )
              .sort-by-trigger(slot="trigger" slot-scope="{ active }")
                BButton(
                  :label="_.find(dropdownOptions, {value:mapType}).label"
                  :icon-right="active ? 'chevron-up' : 'chevron-down'"
                )
              template(v-for="option in dropdownOptions")
                BDropdownItem(
                  v-if="mapTypeAccessableForDropdown(option.value)"
                  :value="option.value"
                ) {{ option.label }} 
                  BTooltip(
                    v-if="option.tooltip"
                    :label="option.tooltip"
                    position="is-top"
                    animated
                    type="is-light"
                    multilined
                  )
                    BIcon(v-if="option.value!=='all'" icon="help-circle-outline" size="is-small")
                

    // Map Option
    template
      BLoading(:active="!programMapsLoaded" :is-full-page="false")
      .block.map-list(v-if="programMapsLoaded")
        .columns.is-multiline.is-mobile.is-centered.is-variable.is-6-fullhd.is-5-widescreen
          .column.is-narrow(
            v-for="map in viewableMaps" :key="map.id"
          )
            .card.map-card.p-3(
              @click="setSelectedMap(map)"
              :class="{ 'is-selected': formData.mapId.value === map.id }"
            )
              //- MAP CHALLENGES HAVE RECTANGULAR IMAGES
              .card-image.card-image-map-challenge.pb-2.is-flex.is-justify-content-center(v-if="!map.isWellnessChallenge")
                figure.image.has-text-centered
                  img.has-border-radius-large(:src="map.image" :alt="map.name")
                  BTooltip.challenge-type-icon-tooltip-wrapper(
                    v-if="!map.isPreventativeChallenge"
                    label="Steps Challenge"
                    position="is-top"
                    animated
                    type="is-light"
                  )
                    WtSvgIcon.challenge-type-icon(icon="icon_orange_two_shoe")
              //- WELLNESS CHALLENGES HAVE SQUARE IMAGES
              .card-image.card-image-wellness-challenge.pb-2.is-flex.is-justify-content-center(v-if="map.isWellnessChallenge")
                figure.image.has-text-centered
                  img.has-border-radius-large(:src="map.image" :alt="map.name")
                  BTooltip.challenge-type-icon-tooltip-wrapper(
                    label="Wellness Challenge"
                    position="is-top"
                    animated
                    type="is-light"
                  )
                    WtSvgIcon.challenge-type-icon( icon="icon_wellness_challenge_type")
              .card-content
                WtTitle.map-name.is-marginless(card-title) {{ map.name }}
                WtButton.map-details(
                  v-if="isAdminBuilder && map.isPreventativeChallenge"
                  color="grey"
                  fullwidth
                ) Select
                WtButton.map-details(
                  v-if="isAdminBuilder && !map.isPreventativeChallenge"
                  @click.native.stop="openDetailsModal(map)"
                  color="grey"
                  fullwidth
                ) Details
            // DETAILS MODAL
            BModal.details-modal(
              :active.sync="detailsModalOpen"
              has-modal-card
              :key="map.id"
              v-if="detailsModalOpenMapId === map.id"
            )
              .modal-card(v-if="map.isWellnessChallenge" style="width: auto")
                .modal-card-body.modal-card-body-no-head-no-foot
                  article.media
                    .media-left
                      figure.image.is-128x128
                        img(:src="map.image" alt="wellness challenge image")
                      WtTitle(card-title).pt-4.mb-2.resources-title.is-family-secondary(v-if="!map.isCustomWellnessChallenge") Resources
                      .resources-tab.is-family-secondary
                        a(v-if="map.poster" :href="map.poster" rel="noopener noreferrer" target="_blank").is-size-7.has-text-underlined.has-text-teal Download Poster
                        a(v-if="map.emailTemplate" :href="map.emailTemplate" rel="noopener noreferrer" target="_blank").is-size-7.has-text-underlined.has-text-teal Download Email Templates
                    .media-content
                      .media-content-head
                        .is-flex.is-align-content-center
                          .is-size-6.has-text-grey.pr-3 Wellness Challenge
                          BTooltip(
                            v-if="!map.isCustomWellnessChallenge"
                            position="is-bottom"
                            multilined
                            animated
                            type="is-light"
                            size="is-large"
                            :triggers="['click']"
                            :auto-close="['outside', 'escape']"
                          )
                            .is-size-7.has-text-underlined.has-text-teal.pointer-on-hover What's this?
                            template(slot="content")
                              p {{ wellnessChallengeDetailsText }}

                        WtTitle(h3) {{ map.name }}
                        WtIconButton.close-button(@click.native="detailsModalOpenMapId = null" aria-label="Close modal")
                          BIcon(icon="close-circle" type="is-grey-light" size="is-medium")
                      .content
                        WtDisplayRichTextHTML.pb-4(:html="map.description")
                        WtTitle.mb-0(card-title) How to Earn Points in {{ map.name }}
                        .is-size-6.has-text-grey.pb-3(v-if="!map.isCustomWellnessChallenge") During the six weeks of the challenge, your participants earn points in the following ways:
                        .p-3.has-background-light.has-border-radius-large
                          WellnessChallengePointsBreakDownDetails(
                            :metricsTracking="map.metricsTracking"
                            :isCustomWellness="Boolean(map.isCustomWellnessChallenge)"
                          )
                          .pt-3(v-if="!map.isCustomWellnessChallenge")
                            .is-size-5.has-text-weight-bold.is-family-secondary Total Potential Challenge Points:
                              span.pl-1.has-text-weight-light.is-family-primary {{ map.totalPointsPossible}}
                            .is-size-5.has-text-weight-bold.is-family-secondary Total Challenge Points Needed to Reach Challenge Goal:
                              span.pl-1.has-text-weight-light.is-family-primary {{ map.totalPointsGoal }}
              .modal-card(v-else-if="map.isPreventativeChallenge" style="width: auto")
                .modal-card-body.modal-card-body-no-head-no-foot
                  article.media
                    .media-left
                      figure.image.is-128x128
                        img(:src="map.image" alt="step challenge image")
                    .media-content
                      .media-content-head
                        .is-flex.is-align-content-center
                          .is-size-6.has-text-grey.pr-3 Preventative Care Challenge
                        WtTitle(h3) {{ map.name }}
                        WtIconButton.close-button(@click.native="detailsModalOpenMapId = null" aria-label="Close modal")
                          BIcon(icon="close-circle" type="is-grey-light" size="is-medium")
              .modal-card(v-else style="width: auto")
                .modal-card-body.modal-card-body-no-head-no-foot
                  article.media
                    .media-left
                      figure.image.is-128x128
                        img(:src="map.image" alt="step challenge image")
                      WtTitle(card-title).pt-4.mb-2.resources-title.is-family-secondary(v-if="!map.isCustomWellnessChallenge") Resources
                      .resources-tab.is-family-secondary
                        a(v-if="map.poster" :href="map.poster" rel="noopener noreferrer" target="_blank").is-size-7.has-text-underlined.has-text-teal Download Poster
                        a(v-if="map.emailTemplate" :href="map.emailTemplate" rel="noopener noreferrer" target="_blank").is-size-7.has-text-underlined.has-text-teal Download Email Templates
                    .media-content
                      .media-content-head
                        .is-flex.is-align-content-center
                          .is-size-6.has-text-grey.pr-3 Step Challenge
                        WtTitle(h3) {{ map.name }}
                        WtIconButton.close-button(@click.native="detailsModalOpenMapId = null" aria-label="Close modal")
                          BIcon(icon="close-circle" type="is-grey-light" size="is-medium")
                      .content
                        WtDisplayRichTextHTML.pb-4(:html="map.description")
                        MapMilestones(:map-id="map.id")
          template(v-if="showChartBasedStuff")
            .column.is-narrow
              .card.map-card.p-3(
                        @click="selectChartBased()"
                        :class="{ 'is-selected': isChartChallengeSelected }"
                      )
                .card-image.card-image-map-challenge.pb-2.is-flex.is-justify-content-center
                  figure.image.has-text-centered
                    img.has-border-radius-large(:src="chartChallengeImagePlaceholder")
                .card-content
                  WtTitle.map-name.is-marginless(card-title) {{ chartBasedChallengeTitle }}
                  WtButton.map-details(
                    v-if="isAdminBuilder"
                    color="grey"
                    fullwidth
                  ) Select

      .empty-state(v-if="programMapsLoaded && _.isEmpty(viewableMaps) && !showChartBasedStuff")
        p Hey there,
        p Hmm, looks like no maps to show.
        p Please try again, or reach out to your admin, or to wellness@be-engaged.co

    ChallengeBuilderControls(
      :step-to-activate="2"
      show-cancel
      :allow-next="(!!formData.mapId.value && isMapChallenge) || (isChartChallengeSelected && isChartOrAllType)"
    )
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";
import WtIconButton from "@@/WtIconButton/WtIconButton";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import WtDisplayRichTextHTML from "@@/WtDisplayRichTextHTML/WtDisplayRichTextHTML";
import WellnessChallengePointsBreakDownDetails from "@@/Challenge/WellnessChallenge/WellnessChallengePointsBreakDownDetails";
import ChallengeBuilderControls from "./ChallengeBuilderControls";
import { ContentLoader } from "vue-content-loader";
import MapMilestones from "@@/ChallengeBuilder/MapMilestones";

export default {
  name: "ChallengeBuilderType",

  components: {
    ChallengeBuilderControls,
    WtButton,
    WtIconButton,
    WtTitle,
    WtSvgIcon,
    WtDisplayRichTextHTML,
    ContentLoader,
    WellnessChallengePointsBreakDownDetails,
    MapMilestones
  },

  data() {
    return {
      mapType: "all",
      mapQuery: "",
      detailsModalOpen: false,
      detailsModalOpenMapId: null,
      isChartChallengeSelected: false,
      chartBasedChallengeTitle: "Chart-based Challenge",
      dropdownOptions: [
        {
          value: "all",
          label: "All Challenges",
          tooltip: false
        },
        {
          value: "steps",
          label: "Movement",
          tooltip: `In a Movement challenge, participants track steps and activities
             to compete with themselves or against colleagues and friends. Progress 
             is tracked automatically and results are posted on a virtual map and 
             leaderboard so participants can see how they’re doing.`
        },
        {
          value: "wellness",
          label: "Wellness",
          tooltip: `Wellness Challenges focus on encouraging healthy habits using Walker
            Tracker's wellness tracking tools. During a wellness challenge, points are 
            awarded for taking daily action on the selected metrics, reflecting on progress, 
            and answering related questions.`
        },
        {
          value: "preventative",
          label: "Preventative Care",
          tooltip: `In a Preventative Care Challenge, participants will track the date of event 
            of when they attended or completed the activity as described, members may earn points 
            for completing this challenge.`
        },
        {
          value: "customWellness",
          label: "Custom Wellness",
          tooltip: `Track a variety of custom health actions and create your own activity. 
            Just describe your goal in 50 characters or less and choose from a list of healthy 
            action-forming habits and set a custom points goal.`
        },
        {
          value: "chartBased",
          label: "Chart Based",
          tooltip: `Run a simple chart-based activity challenge with a focus on leaderboards.`
        }
      ]
    };
  },

  computed: {
    ...mapGetters("authMember", ["isWtAdmin"]),
    ...mapGetters("program", ["maps", "programMapsLoaded", "isCoreTier"]),
    ...mapGetters("challengeBuilder", [
      "options",
      "formData",
      "memberMode",
      "teamMode"
    ]),

    isMapChallenge() {
      return this.mapType !== "chartBased";
    },

    isChartOrAllType() {
      return this.mapType === "all" || this.mapType === "chartBased";
    },

    chartChallengeImagePlaceholder() {
      return "https://walkertracker.s3.amazonaws.com/newui/placeholder/challenge_placeholder.jpg";
    },

    showWellnessStuff() {
      return (
        this.isAdminBuilder && this.$auth.user().program.isWellnessMetricsOn
      );
    },

    showPreventativeStuff() {
      return this.isAdminBuilder && !this.isCoreTier;
    },

    showChartBasedStuff() {
      return (
        this.isAdminBuilder &&
        this.isChartOrAllType &&
        this.searchFilterForChartBased
      );
    },

    searchFilterForChartBased() {
      const chartBasedTitleLower = this.chartBasedChallengeTitle.toLowerCase();
      const mapQueryLower = this.mapQuery.toLowerCase();

      return (
        mapQueryLower === "" ||
        chartBasedTitleLower.search(mapQueryLower) !== -1
      );
    },

    displayMapTypeDropdown() {
      return (
        this.showWellnessStuff ||
        this.showPreventativeStuff ||
        this.isAdminBuilder
      );
    },

    wellnessChallengeDetailsText() {
      return `Wellness Challenges focus on encouraging healthy habits using Be Well's
      wellness tracking tools. Wellness Challenges award points for taking daily action on the
      selected metrics. Each Wellness Challenge is configured to track journal entries (posting photos and/or written entries) and answering pop-up questions
      related to the challenge wellbeing theme.`;
    },

    isAdminBuilder() {
      return /admin/.test(this.$route.path);
    },

    viewableMaps() {
      let viewableMaps = this.isAdminBuilder
        ? this.maps
        : this.maps.filter(map => map.userViewable && !map.isWellnessChallenge);

      let movementMaps = viewableMaps.filter(
        map =>
          !map.isWellnessChallenge &&
          !map.isPreventativeChallenge &&
          !map.isCustomWellnessChallenge
      );
      let wellnessMaps = viewableMaps.filter(
        map => map.isWellnessChallenge && !map.isCustomWellnessChallenge
      );
      let preventativeMaps = viewableMaps.filter(
        map => map.isPreventativeChallenge
      );
      let customWellnessMaps = viewableMaps.filter(
        map => map.isCustomWellnessChallenge
      );
      // Let's display maps in alpha order (keeping wellness maps first, preventative second).
      let sortedWellnessMaps = this._.sortBy(wellnessMaps, "name");
      let sortedMovementMaps = this._.sortBy(movementMaps, "name");
      let sortedPreventativeMaps = this._.sortBy(preventativeMaps, "name");
      let sortedCustomWellnessMaps = this._.sortBy(customWellnessMaps, "name");
      viewableMaps = [
        ...sortedWellnessMaps,
        ...sortedPreventativeMaps,
        ...sortedMovementMaps,
        ...sortedCustomWellnessMaps
      ];

      if (this.mapType === "chartBased") {
        return [];
      }

      if (
        this.mapType === "all" &&
        !this.showWellnessStuff &&
        this.showPreventativeStuff
      ) {
        return viewableMaps.filter(
          map => !map.isWellnessChallenge && !map.isCustomWellnessChallenge
        );
      }

      if (
        this.mapType === "all" &&
        this.showWellnessStuff &&
        !this.showPreventativeStuff
      ) {
        return viewableMaps.filter(map => !map.isPreventativeChallenge);
      }

      if (
        this.mapType === "steps" ||
        (!this.showWellnessStuff && !this.showPreventativeStuff)
      ) {
        return viewableMaps.filter(
          map =>
            !map.isWellnessChallenge &&
            !map.isCustomWellnessChallenge &&
            !map.isPreventativeChallenge
        );
      }
      if (this.mapType === "wellness") {
        return viewableMaps.filter(
          map => map.isWellnessChallenge && !map.isCustomWellnessChallenge
        );
      }
      if (this.mapType === "preventative") {
        return viewableMaps.filter(map => map.isPreventativeChallenge);
      }
      if (this.mapType === "customWellness") {
        return viewableMaps.filter(map => map.isCustomWellnessChallenge);
      }
      if (this.mapType === "all") {
        return viewableMaps;
      }

      return viewableMaps;
    }
  },

  async mounted() {
    if (!this.programMapsLoaded) {
      await this.fetchProgramMaps();
    }

    this.triggerMapSearch = this._.debounce(() => {
      if (this.mapQuery) {
        this.searchMaps({ query: this.mapQuery });
      } else {
        this.fetchProgramMaps();
      }
    }, 700);
  },

  methods: {
    ...mapActions("program", ["fetchProgramMaps", "searchMaps"]),
    ...mapActions("challengeBuilder", ["updateFormData", "updateOption"]),

    setSelectedMap(map) {
      this.isChartChallengeSelected = false;
      this.updateFormData({ key: "mapId", value: map.id });
      this.updateFormData({ key: "challengeName", value: map.name });

      if (map.isWellnessChallenge) {
        // ==============================================
        // NOTE: wellness challenges are ALWAYS featured
        // ==============================================
        this.updateFormData({ key: "featured", value: 1 });
      }
      this.$scrollTo("#challenge-builder-controls");
    },

    selectChartBased() {
      this.isChartChallengeSelected = true;
      this.updateFormData({ key: "mapId", value: null });
      this.updateFormData({
        key: "challengeName",
        value: this.chartBasedChallengeTitle
      });
      this.$scrollTo("#challenge-builder-controls");
    },

    triggerMapSearch() {
      // defined in mounted lifecycle hook
      // reason being that debounced returns a function that needs
      // to be called on keyup, in order for it to have access to the vue
      // instance we have to reference debounce on the Vue instance
      // rather than globally
    },

    openDetailsModal(map) {
      this.detailsModalOpen = true;
      this.detailsModalOpenMapId = map.id;
    },

    // TODO add logic to chart-based maps visibility once chart-based
    // builder is done
    mapTypeAccessableForDropdown(type) {
      if (type === "wellness" || type === "customWellness") {
        return this.showWellnessStuff;
      } else if (type === "preventative") {
        return this.showPreventativeStuff;
      } else if (type === "chartBased") {
        return this.isAdminBuilder;
      }
      return true;
    }
  }
};
</script>

<style lang="sass" scoped>
#challenge-builder-type
  .pointer-on-hover:hover
    cursor: pointer
  .map-type-info-dropdown
    z-index: 1
    position: absolute
    min-width: 250px
    background-color: $white
    padding: $size-7
    border-radius: $radius-large
    box-shadow: $box-shadow
    +mobile
      min-width: 99vw
  .search-map-field
    min-width: 4rem
  .filter-map-field
    min-width: 4rem
  .map-card
    width: 186px
    cursor: pointer
    overflow: visible
    &:hover
      box-shadow: 0 1px 7px 2px hsla(203.9, 85.8%, 52.9%, 0.1), 0 2px 10px 0 rgba(189, 189, 189, 0.2)
    .card-image
      .challenge-type-icon-tooltip-wrapper
        position: absolute
        bottom: .5rem
        right: .5rem
        .challenge-type-icon /deep/ svg
          box-shadow:  0 12px 18px 0 rgba(0,0,0,0.2)
          border-radius: $radius-large
      &.card-image-map-challenge
        img
          width: 178px
          height: 134px
      &.card-image-wellness-challenge
        img
          width: 134px
          height: 134px
    .card-content
      padding: 1rem
      display: flex
      flex-direction: column
      .map-name
        height: 101px
        overflow: hidden
        text-overflow: ellipsis
        display: -webkit-box
        -webkit-line-clamp: 5
        -webkit-box-orient: vertical
      .map-details
        margin-top: auto

  .map-card.is-selected
      box-shadow: 0 3px 10px 3px hsla(203.9, 85.8%, 52.9%, 0.5), 0 5px 20px 0 rgba(189, 189, 189, 0.5)
  .details-modal
    .close-button
      position: absolute
      top: .5rem
      right: .5rem
    .modal-card-body.modal-card-body-no-head-no-foot
      border-radius: $radius-large
      padding-right: 3rem // account for close button
    .modal-card-head, .modal-card-foot
      padding: .5rem
    .resources-tab
      display: flex
      flex-direction: column
      max-width: 120px
      font-size: 12px
    .resources-title
      font-size: 16px
  .max-witdh-100
    max-width: 100%
  .search-map-field
    @media screen and (max-width: 1490px) and (min-width: 769px)
      margin-top: 20px
  .filter-map-field
    @media screen and (max-width: 1490px) and (min-width: 769px)
      margin-top: 20px
  .wider-field
    width: 300px
</style>
